<script setup lang="ts">
type IapCardFact = {
  title: string
  content: string
  contentColor?: string
}

type GuaranteePopData = {
  cardIcon?: string
  compensationAmount?: string
  compensationAmountColor?: string
  guardStatusTextColor?: string
  gradientBgColor1?: string
  gradientBgColor2?: string
  listData?: IapCardFact[]
  type?: number | string
  fundLabel?: string
}

const props = defineProps<{
  traderData?: any
  traderCode?: string
  guaranteePop?: Partial<GuaranteePopData>
}>()

const emit = defineEmits<{
  open: []
}>()

const { t } = useAppI18n()
const guaranteePop = computed(() => props.guaranteePop || {})
const bitGuarantee = computed(() => props.traderData?.bitGuarantee || {})

const iapCardFacts = computed(() => {
  const listData = bitGuarantee.value?.items || []
  const count = bitGuarantee.value?.type === 0 ? 2 : 1

  return listData.filter((item) => item?.title || item?.content).slice(0, count)
})

const themeColor = computed(
  () => formatColor(guaranteePop.value?.guardStatusTextColor) || formatColor(iapCardFacts.value[0]?.contentColor),
)

const cardStyle = computed(() => ({
  '--iap-card-background':
    Number(bitGuarantee.value?.type) === 1 ? '#fff' : 'linear-gradient(180deg, #f5faf8 0%, #fff 80%)',
  '--iap-card-gradient-bg-1': guaranteePop.value?.gradientBgColor1 || '#eaf6f1',
  '--iap-card-gradient-bg-2': guaranteePop.value?.gradientBgColor2 || '#fff',
  '--iap-card-brand-color': themeColor.value || '#1f845a',
}))

const open = () => {
  emit('open')
}

const headerRef = ref<HTMLElement | null>(null)
const measureRef = ref<HTMLElement | null>(null)

// 1. SSR: Estimate text width based on CJK/Latin to avoid initial flash
const getInitialShowFullName = () => {
  // if (bitGuarantee.value?.type !== 0) return false
  const name = props.traderData?.basicInfo?.shortNameLocal || ''
  const fullText = t('83728', [name])

  let approxWidth = 0
  for (let i = 0; i < fullText.length; i++) {
    const code = fullText.charCodeAt(i)
    if (
      (code >= 0x4e00 && code <= 0x9fff) ||
      (code >= 0x3040 && code <= 0x30ff) ||
      (code >= 0xac00 && code <= 0xd7a3)
    ) {
      approxWidth += 18
    } else {
      approxWidth += 10
    }
  }
  return approxWidth <= 140
}

const showFullName = ref(getInitialShowFullName())

let resizeObserver: ResizeObserver | null = null

// 2. Client: Exact DOM measurement to correct the guess
const checkOverflow = () => {
  if (!measureRef.value) return
  showFullName.value = measureRef.value.scrollWidth <= measureRef.value.clientWidth
}

onMounted(() => {
  if (bitGuarantee.value?.type === 0) {
    checkOverflow()
    if (headerRef.value) {
      resizeObserver = new ResizeObserver(() => checkOverflow())
      resizeObserver.observe(headerRef.value)
    }
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<template>
  <section
    class="exchange-iap-card"
    :style="cardStyle"
    role="button"
    tabindex="0"
    aria-labelledby="exchange-iap-card-title"
    @click.stop="open"
    @keydown.enter.stop.prevent="open"
    @keydown.space.stop.prevent="open"
  >
    <div class="exchange-iap-card__main">
      <header class="exchange-iap-card__header relative" ref="headerRef">
        <h2 id="exchange-iap-card-title" class="exchange-iap-card__title" style="min-width: 0; flex: 1">
          <span
            :style="{
              color: bitGuarantee?.type === 0 ? '#1f845a' : '#112D73',
            }"
            style="flex-shrink: 0"
            >IAP</span
          >
          <span
            v-if="bitGuarantee?.type === 0"
            class="exchange-iap-card__title-text"
            style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1"
            >{{ t('83728', showFullName ? [props.traderData?.basicInfo?.shortNameLocal] : []) }}</span
          >
          <span v-else class="exchange-iap-card__title-text" style="flex-shrink: 0">{{ t('83729') }} </span>
        </h2>

        <!-- Hidden measurement clone -->
        <h2
          class="exchange-iap-card__title pointer-events-none absolute opacity-0"
          style="min-width: 0; flex: 1; left: 0; top: 0; width: calc(100% - 20px); z-index: -1"
          aria-hidden="true"
        >
          <span style="flex-shrink: 0">IAP</span>
          <span
            v-if="bitGuarantee?.type === 0"
            ref="measureRef"
            class="exchange-iap-card__title-text"
            style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1"
            >{{ t('83728', [props.traderData?.basicInfo?.shortNameLocal]) }}</span
          >
          <span v-else class="exchange-iap-card__title-text" style="flex-shrink: 0">{{ t('83729') }} </span>
        </h2>
      </header>

      <div class="exchange-iap-card__fund">
        <CommonImage
          v-if="bitGuarantee.exchangeIcon || bitGuarantee.platformIcon"
          :src="bitGuarantee.type === 0 ? bitGuarantee.exchangeIcon : bitGuarantee.platformIcon"
          alt=""
          class="exchange-icon rounded"
        />
        <strong :style="{ color: formatColor(bitGuarantee?.compensationAmountColor) || undefined }">
          {{ bitGuarantee?.exchangeGuaranteeAmount }}
        </strong>
      </div>
    </div>

    <dl v-if="iapCardFacts.length" class="exchange-iap-card__facts">
      <div
        v-for="item in iapCardFacts"
        :key="`${item.title}-${item.content}`"
        class="exchange-iap-card__fact"
        :class="{ 'exchange-iap-card__fact--vertical': bitGuarantee?.type === 1 }"
      >
        <dt>{{ item.title }}</dt>
        <dd :style="{ color: formatColor(item.iapCompensationTextColor) || undefined }">{{ item.content }}</dd>
      </div>
    </dl>
  </section>
</template>

<style scoped lang="less">
.exchange-icon {
  width: 24px;
  height: 24px;
}
.exchange-iap-card {
  display: flex;
  height: 212px;
  min-height: 212px;
  width: 100%;
  flex-direction: column;
  gap: 16px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: var(--iap-card-background);
  cursor: pointer;
  padding: 20px;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.exchange-iap-card:hover {
  box-shadow: 0 12px 32px rgba(8, 19, 62, 0.08);
}

.exchange-iap-card:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--iap-card-brand-color) 24%, transparent);
  outline-offset: 2px;
}

.exchange-iap-card__main {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
}

.exchange-iap-card__header {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
}

.exchange-iap-card__title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  margin: 0;
  color: #292a2e;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exchange-iap-card__title span:first-child {
  color: var(--iap-card-brand-color);
  flex-shrink: 0;
}

.exchange-iap-card__title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exchange-iap-card__info-icon {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
}

.exchange-iap-card__fund {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  color: #292a2e;
  font-family: WikiFX, Outfit, sans-serif;
  white-space: nowrap;
}

// .exchange-iap-card__fund img {
//   width: 24px;
//   height: 24px;
//   flex: 0 0 auto;
//   border-radius: 50%;
// }

.exchange-iap-card__fund strong {
  overflow: hidden;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  text-overflow: ellipsis;
}

.exchange-iap-card__fund span {
  flex: 0 0 auto;
  color: #7d818a;
  font-family:
    Outfit,
    PingFang SC,
    sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.exchange-iap-card__facts {
  position: relative;
  display: flex;
  width: 100%;
  overflow: hidden;
  flex-direction: column;
  flex: 0 0 auto;
  border-radius: 8px 8px 0 0;
  background: linear-gradient(180deg, var(--iap-card-gradient-bg-1) 0%, var(--iap-card-gradient-bg-2) 100%);
  box-shadow: inset 0 4px 5px rgba(255, 255, 255, 0.55);
  margin: 0;
  padding: 0 16px;
}

.exchange-iap-card__facts::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 8px 8px 0 0;
  padding: 1px;
  background: linear-gradient(180deg, var(--iap-card-gradient-bg-1) 0%, var(--iap-card-gradient-bg-2) 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.exchange-iap-card__fact {
  display: flex;
  height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}

.exchange-iap-card__fact dt,
.exchange-iap-card__fact dd {
  margin: 0;
}

.exchange-iap-card__fact dt {
  min-width: 0;
  overflow: hidden;
  color: #505258;
  font-weight: 400;
  text-overflow: ellipsis;
}

.exchange-iap-card__fact dd {
  flex: 0 0 auto;
  color: #292a2e;
  font-weight: 500;
  text-align: right;
}

.exchange-iap-card__fact--vertical {
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: auto;
  height: 82px;
  gap: 4px;
}

.exchange-iap-card__fact--vertical dt,
.exchange-iap-card__fact--vertical dd {
  width: 100%;
}

.exchange-iap-card__fact--vertical dd {
  text-align: left;
}
</style>
